import { useConfig } from "./useConfig"
import { newSearch, simulateUpdateSearch, updateSearch } from "../actions"
import { useStore } from "./useStore"
import { useActions as useBaseActions } from "@nosto/search-js/preact/hooks"
import { SearchQuery, SearchOptions } from "../api"
import type { InputSearchRangeFilter } from "@nosto/nosto-js/client"

/**
 * Preact hook that import current actions to the component.
 * Actions should be used to modify state, making new searches, etc.
 *
 * **Should always be at the top of the component!**
 *
 * @example
 * ```jsx
 * import { useActions } from '@nosto/preact'
 *
 * export default () => {
 *     const { newSearch } = useActions()
 *
 *     return <button onClick={() => {
 *         newSearch({
 *             query: {
 *                 query: "samsung"
 *             }
 *         })
 *     }}>
 *         Search for "samsung"
 *     </button>
 * }
 * ```
 *
 * @group Hooks
 */
export function useActions() {
    const context = {
        config: useConfig(),
        store: useStore()
    }

    const { toggleProductFilter, replaceFilter } = useBaseActions()

    return {
        updateSearch(query: SearchQuery, options?: SearchOptions) {
            return updateSearch(context, query, options)
        },
        newSearch(query: SearchQuery, options?: SearchOptions) {
            return newSearch(context, query, options)
        },
        toggleProductFilter(field: string, value: string, active: boolean) {
            return toggleProductFilter(field, value, active)
        },
        replaceFilter(field: string, value: InputSearchRangeFilter | string | undefined) {
            return replaceFilter(field, value)
        },
        simulateUpdateSearch(query: SearchQuery, options?: SearchOptions) {
            return simulateUpdateSearch(context, query, options)
        }
    }
}
